import { ChangeDetectionStrategy, Component, computed, input, linkedSignal, signal, viewChild } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ButtonComponent } from "../../standalone/s25.button.component";
import { S25EditableModule } from "../s25-editable/s25.editable.module";
import { S25IconButtonComponent } from "../../standalone/s25.icon.button.component";
import { CustomAttributes } from "../../pojo/CustomAttributes";
import { S25EditableCustomAttributeComponent } from "../s25-editable/s25.editable-custom-attribute/s25.editable-custom-attribute.component";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { Item } from "../../pojo/Item";
import { StudentEngagementService } from "./student.engagement.service";
import { Bind } from "../../decorators/bind.decorator";
import { S25Util } from "../../util/s25-util";
import { OrganizationMicroService } from "../../services/organization.micro.service";
import { S25File } from "../s25-file-upload/s25.file.upload.component";
import { AttachmentService } from "../../services/attachment.service";
import { EventMicroService } from "../../services/event.micro.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { Attribute } from "../s25-event/EventMicroI";

@TypeManagerDecorator("se-editable-logo")
@Component({
    selector: "se-editable-logo",
    template: ` @if (imageUrl()) {
            <img [src]="imageUrl()" alt="Object Image" class="object-image" />
        } @else {
            <div
                class="image-placeholder"
                [class]="{
                    event: itemTypeId() === itemIds.Event,
                    org: itemTypeId() === itemIds.Organization,
                }"
            >
                <s25-ng-icon [type]="iconProp()" [size]="80"></s25-ng-icon>
            </div>
        }
        @if (showImageForm()) {
            <div class="image-form-container">
                <div class="image-form">
                    <div class="form-header">
                        <h4>{{ header() }}</h4>
                        <s25-ng-icon-button
                            [type]="'close'"
                            [size]="20"
                            (click)="showImageForm.set(false); $event.stopPropagation()"
                        ></s25-ng-icon-button>
                    </div>

                    <div class="form-content">
                        <s25-ng-editable-custom-attribute
                            [attributeId]="logoAttrId()"
                            [attributeType]="urlAttrType"
                            [value]="imageAttr()?.value"
                            [hasUploadFile]="true"
                            [objectId]="itemId()"
                            [itemTypeId]="itemTypeId()"
                            [readOnly]="false"
                        ></s25-ng-editable-custom-attribute>
                    </div>
                    @if (imageErrorMsg()) {
                        <p class="ngRed">{{ imageErrorMsg() }}</p>
                    }
                    <div class="form-actions">
                        <button
                            type="button"
                            class="btn btn-secondary"
                            (click)="showImageForm.set(false); $event.stopPropagation()"
                        >
                            Cancel
                        </button>
                        <s25-ng-button [type]="'primary'" [onClick]="updateLogo" (click)="$event.stopPropagation()"
                            >Save</s25-ng-button
                        >
                        @if (imageUrl()) {
                            <s25-ng-button [type]="'danger'" [onClick]="deleteLogo" (click)="$event.stopPropagation()"
                                >Delete Logo</s25-ng-button
                            >
                        }
                    </div>
                </div>
            </div>
        }`,
    host: {
        "(click)": "canEdit() &&  showImageForm.set(true)",
        "[class.editing]": "showImageForm()",
    },
    styles: `
        :host {
            position: relative;
        }

        :host.editing {
            cursor: default;
        }

        .object-image {
            max-width: 300px;
            width: 100%;
            height: auto;
            border-radius: 12px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .image-placeholder {
            width: 300px;
            height: 200px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

            &.event {
                background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
                border: 3px dashed #90caf9;
            }

            &.org {
                background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
                border: 2px dashed #c3cfe2;
            }
        }

        ::ng-deep .image-placeholder.org svg {
            color: #667eea;
            opacity: 0.7;
        }

        ::ng-deep .image-placeholder.event svg {
            color: #2196f3;
            opacity: 0.7;
        }

        .image-form-container {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            border-radius: 12px;
            width: 23em;
        }

        .image-form {
            background: white;
            border-radius: 8px;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
            width: 400px;
            max-width: 90vw;
            max-height: 80vh;
            overflow-y: auto;
            position: relative;
        }

        .form-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem;
            border-bottom: 1px solid #e0e0e0;
        }

        .form-header h4 {
            margin: 0;
            font-size: 1.125rem;
            font-weight: 600;
            color: #333;
        }

        .form-content {
            padding: 0.5rem 1.25rem;
        }

        .form-group label {
            display: block;
            margin-bottom: 0.5rem;
            font-weight: 500;
            color: #555;
            font-size: 0.875rem;
        }

        .form-divider span {
            background: white;
            padding: 0 1rem;
            position: relative;
            color: #999;
            font-size: 0.875rem;
        }

        .image-preview img {
            max-width: 100%;
            max-height: 200px;
            border-radius: 6px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .form-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 1.25rem;
            border-top: 1px solid #e0e0e0;
        }

        .btn {
            padding: 0.5rem 1.25rem;
            border: none;
            display: inline-flex;
            align-items: center;
            gap: 0.5rem;
            border-radius: 6px;
            font-size: 0.875rem;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
        }

        .btn-secondary {
            background: #f5f5f5;
            color: #666;
        }

        .btn-secondary:hover {
            background: #e0e0e0;
        }

        ::ng-deep .image-form-container s25-ng-editable-custom-attribute label[for="customAttributeValue"] {
            display: none;
        }

        ::ng-deep .image-form-container s25-ng-editable-custom-attribute .file-wrapper {
            display: block;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconButtonComponent, S25ButtonComponent, S25EditableModule, S25IconComponent],
})
export class SeEditableLogo {
    itemId = input.required<number>();
    itemTypeId = input.required<Item.Ids>();
    imageAttr = input<Attribute>();
    canEdit = input<boolean>();

    logoAttrId = computed(() => StudentEngagementService.LogoAttrIds[this.itemTypeId()]);
    protected iconProp = computed(() => (this.itemTypeId() === Item.Ids.Event ? "calendarDate" : "starFilled"));
    protected header = computed(() => `Update ${this.itemTypeId() === Item.Ids.Event ? "Event" : "Organization"} Logo`);
    protected itemIds = Item.Ids;
    urlAttrType = CustomAttributes.type.URL;
    attrComp = viewChild(S25EditableCustomAttributeComponent);

    showImageForm = signal(false);
    imageUrl = linkedSignal<string>(() => StudentEngagementService.parseUrl(this.imageAttr()?.value));
    imageErrorMsg = signal<string>("");

    promiseMap: Partial<Record<Item.Ids, Function>> = {
        [Item.Ids.Event]: EventMicroService.putEventAttributes,
        [Item.Ids.Organization]: OrganizationMicroService.saveOrganization,
    };

    @Bind
    async updateLogo() {
        if (!this.attrComp) return;
        let value: string | void;
        if (this.attrComp().file?.fileRef) value = await this.processFile(this.attrComp().file);
        else value = <string>this.attrComp().value;
        if (value) {
            const payload = [
                {
                    id: +this.itemId(),
                    attributes: [{ attributeId: this.logoAttrId(), value }],
                },
                ...(this.itemTypeId() === Item.Ids.Event ? [this.itemId()] : []), // event microservice needs eventId as 2nd param
            ];
            const [_, error] = await S25Util.Maybe(this.promiseMap[this.itemTypeId()](...payload));
            if (error) {
                this.imageErrorMsg.set(S25Util.errorText(error));
                return console.error(error);
            }
            this.imageErrorMsg.set("");
            this.imageUrl.set(CustomAttributeService.getCAFilePreviewUrl(value));
            this.showImageForm.set(false);
        }
    }

    async processFile(file: S25File) {
        const [_, error] = await S25Util.Maybe(
            AttachmentService.postFile(this.itemTypeId(), this.itemId(), file.fileRef, file.name, this.logoAttrId()),
        );
        if (error) {
            this.imageErrorMsg.set(S25Util.errorText(error));
            return console.error(error);
        }
        this.imageErrorMsg.set("");
        return `object_type=${this.itemTypeId()}&object_id=${this.itemId()}&atrb_id=${this.logoAttrId()}&file_name=${file.name}`;
    }

    @Bind
    async deleteLogo() {
        const payload = [
            {
                id: this.itemId(),
                remove: {
                    attributes: [{ attributeId: this.logoAttrId }],
                },
            },
            ...(this.itemTypeId() === Item.Ids.Event ? [this.itemId()] : []),
        ]; // event microservice needs eventId as 2nd param;
        const [_, error] = await S25Util.Maybe(this.promiseMap[this.itemTypeId()](...payload));
        if (error) {
            this.imageErrorMsg.set(S25Util.errorText(error));
            return console.error(error);
        }
        this.imageErrorMsg.set("");
        this.imageUrl.set("");
        this.showImageForm.set(false);
    }
}
